import { CreatePostBody, FormState, PlatformKey, PostForMeAccount, DEFAULT_CONFIGS, getEffectiveForPlatform, deriveSelectedPlatforms, TikTokConfig } from "./models";

export async function getPostForMeAuthUrl(platform: string, connectionType?: string): Promise<string> {
  const params = new URLSearchParams();
  if (connectionType) {
    params.set('connectionType', connectionType);
  }
  const query = params.toString();
  const url = `/api/postforme/auth-url/${encodeURIComponent(platform)}${query ? `?${query}` : ''}`;

  const res = await fetch(url, {
    credentials: "include"
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || "Failed to get auth URL");
  }
  const data = await res.json();
  return data.url as string;
}

export async function fetchAccounts(): Promise<PostForMeAccount[]> {
  const res = await fetch(`/api/postforme/accounts`, { credentials: "include" });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || "Failed to load accounts");
  }
  const data = await res.json();
  return data as PostForMeAccount[];
}

export function buildCreatePostBody(state: FormState, accounts: PostForMeAccount[]): CreatePostBody {
  const selectedPlatforms = deriveSelectedPlatforms(accounts, state.selectedAccountIds);

  const platform_configurations: CreatePostBody["platform_configurations"] = {};

  selectedPlatforms.forEach((p) => {
    const eff = getEffectiveForPlatform(state, p as PlatformKey);
    const override = state.platforms[p as PlatformKey];
    const cfg = (override?.config ?? DEFAULT_CONFIGS[p as PlatformKey]) as any;

    const base: any = { placement: eff.placement };

    if (override?.captionTouched && eff.placement === "timeline") {
      base.caption = override.caption ?? "";
    }

    if (override?.mediaTouched) {
      base.media = (override.media ?? []).slice(0, 10).map((url) => ({ url }));
    }

    if (p === "facebook") {
      if (cfg.location && cfg.location.trim()) base.location = cfg.location.trim();
      const collab = (cfg.collaborators || "")
        .split(",")
        .map((s: string) => s.trim())
        .filter(Boolean);
      if (collab.length > 0) base.collaborators = collab;
      if (Object.keys(base).length > 0) platform_configurations.facebook = base;
    } else if (p === "instagram") {
      if (cfg.location && cfg.location.trim()) base.location = cfg.location.trim();
      const collab = (cfg.collaborators || "")
        .split(",")
        .map((s: string) => s.trim())
        .filter(Boolean);
      if (collab.length > 0) base.collaborators = collab;
      if (cfg.shareToFeed !== undefined) base.share_to_feed = !!cfg.shareToFeed;
      if (Object.keys(base).length > 0) platform_configurations.instagram = base;
    } else if (p === "tiktok") {
      const tt = mapTikTokConfig(cfg as TikTokConfig, {
        caption: override?.captionTouched ? (override.caption ?? "") : undefined,
        media: override?.mediaTouched ? (override.media ?? []) : eff.media
      });
      if (tt && Object.keys(tt).length > 0) platform_configurations.tiktok = tt;
    }
  });

  const body: CreatePostBody = {
    caption: state.draft.caption ?? "",
    social_accounts: state.selectedAccountIds,
    ...(state.draft.media && state.draft.media.length > 0
      ? { media: state.draft.media.slice(0, 10).map((url) => ({ url })) }
      : {}),
    ...(Object.keys(platform_configurations).length > 0 ? { platform_configurations } : {})
  };

  return body;
}

function mapTikTokConfig(cfg?: TikTokConfig, override?: { caption?: string; media?: string[] }) {
  if (!cfg && !override) return undefined;
  const out: any = {};
  if (override?.caption !== undefined) out.caption = override.caption;
  if (override?.media !== undefined) out.media = (override.media ?? []).map((u) => ({ url: u }));
  if (cfg) {
    out.title = cfg.title ?? undefined;
    out.privacy_status = cfg.privacyStatus ?? "public";
    out.allow_comment = cfg.allowComment ?? true;
    out.allow_duet = cfg.allowDuet ?? true;
    out.allow_stitch = cfg.allowStitch ?? true;
    out.disclose_your_brand = cfg.discloseYourBrand ?? false;
    out.disclose_branded_content = cfg.discloseBrandedContent ?? false;
    out.is_ai_generated = cfg.isAiGenerated ?? false;
    out.is_draft = cfg.isDraft ?? false;
    out.auto_add_music = cfg.autoAddMusic ?? true;
  }
  return out;
}

export async function createPost(body: CreatePostBody): Promise<{ id: string; caption?: string; status?: string }> {
  const res = await fetch(`/api/postforme/posts`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || "Failed to create post");
  }
  return res.json();
}

export async function uploadMedia(file: File): Promise<string> {
  const form = new FormData();
  form.append("file", file);

  const res = await fetch(`/api/postforme/media/upload`, {
    method: "POST",
    credentials: "include",
    body: form
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || "Failed to upload media");
  }
  const data = await res.json();
  return data.mediaUrl as string;
}

export function uploadMediaWithProgress(
  file: File,
  onProgress: (pct: number) => void,
  signal?: AbortSignal
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/postforme/media/upload");
    xhr.withCredentials = true;

    xhr.upload.onprogress = (evt) => {
      if (!evt.lengthComputable) return;
      const pct = Math.round((evt.loaded / evt.total) * 100);
      onProgress(pct);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const json = JSON.parse(xhr.responseText);
          resolve(json.mediaUrl as string);
        } catch {
          reject(new Error("Invalid upload response"));
        }
      } else {
        reject(new Error(xhr.responseText || "Upload failed"));
      }
    };

    xhr.onerror = () => reject(new Error("Upload error"));
    xhr.onabort = () => reject(new Error("Upload canceled"));

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return reject(new Error("Upload canceled"));
      }
      signal.addEventListener("abort", () => xhr.abort());
    }

    const form = new FormData();
    form.append("file", file);
    xhr.send(form);
  });
}

export async function disconnectAllPlatform(platform: string): Promise<void> {
  const res = await fetch(`/api/postforme/accounts/${platform}/disconnect-all`, {
    method: "POST",
    credentials: "include"
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Failed to disconnect all accounts for ${platform}`);
  }
}

const registry = new Map<string, "image" | "video">();

export function getMediaKind(url: string): "image" | "video" | undefined {
  return registry.get(url);
}

export function setMediaKind(url: string, kind: "image" | "video") {
  registry.set(url, kind);
}

export function removeMediaKind(url: string) {
  registry.delete(url);
}
